import { Box, Skeleton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { colors } from '../../const/colors'

export interface IBuildingRow {
  key: string
  name: string
  parkingNames: string[]
  sharedWith: string[]
  overCapacity: boolean
  places: number
  free: number
  reserved: number
  used: number
  pct: number | null
}

const VALUE_INDENT = '4px'

const CELL = {
  fontSize: '13px',
  fontWeight: 700,
  textAlign: 'left' as const,
  pl: VALUE_INDENT,
}
const HEAD = {
  fontSize: '11px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  color: colors.slate,
  fontWeight: 700,
  textAlign: 'left' as const,
}

const ELLIPSIS = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const

const BuildingsTable = ({
  rows,
  pending,
  failed,
  showUtilization,
  snapshotLabel,
  futureDay,
}: {
  rows: IBuildingRow[]
  pending: boolean
  failed: boolean
  showUtilization: boolean
  snapshotLabel: string
  futureDay: boolean
}) => {
  const { t } = useTranslation(['parking_pool_data', 'generic'])

  const columns = `1.7fr repeat(4, minmax(76px, 0.6fr))${showUtilization ? ' 84px' : ''}`

  return (
    <Box
      sx={{
        background: colors.surface,
        border: `1px solid ${colors.border2}`,
        borderRadius: colors.radiusCard,
        boxShadow: colors.shadowCard,
        p: '20px',
        mb: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '12px',
          mb: '14px',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ fontSize: '15px', fontWeight: 800 }}>
          {t('buildings_title')}
        </Box>
        <Box
          sx={{ fontSize: '12px', color: colors.textMuted, fontWeight: 600 }}
        >
          {snapshotLabel}
        </Box>
      </Box>

      {pending ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {[0, 1, 2].map(row => (
            <Box
              key={row}
              sx={{
                display: 'grid',
                gridTemplateColumns: columns,
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <Skeleton variant="rounded" width="70%" height={18} />
              {[0, 1, 2, 3].map(cell => (
                <Skeleton
                  key={cell}
                  variant="rounded"
                  width={32}
                  height={18}
                  sx={{ ml: VALUE_INDENT }}
                />
              ))}
              {showUtilization && <Skeleton variant="rounded" height={10} />}
            </Box>
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <Box
          sx={{ fontSize: '13px', fontWeight: 700, color: colors.textMuted }}
        >
          {failed ? t('stats_error') : t('no_buildings')}
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: '620px' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: columns,
                alignItems: 'center',
                gap: '10px',
                pb: '8px',
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <Box sx={HEAD}>{t('col_building')}</Box>
              <Box sx={HEAD}>{t('col_places')}</Box>
              <Box sx={HEAD}>{t('col_reserved')}</Box>
              <Box sx={HEAD}>{t('col_used')}</Box>
              <Box sx={HEAD}>{t('col_free')}</Box>
              {showUtilization && <Box sx={HEAD}>{t('col_utilization')}</Box>}
            </Box>

            {rows.map(row => (
              <Box
                key={row.key}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: columns,
                  alignItems: 'center',
                  gap: '10px',
                  p: '10px 0',
                  borderBottom: `1px solid ${colors.border2}`,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: '13.5px',
                      fontWeight: 700,
                      color: colors.navy,
                      ...ELLIPSIS,
                    }}
                  >
                    {row.name}
                  </Box>
                  <Box
                    sx={{
                      fontSize: '11.5px',
                      color: colors.textMuted,
                      fontWeight: 600,
                      ...ELLIPSIS,
                    }}
                    title={row.parkingNames.join(', ')}
                  >
                    {row.parkingNames.join(', ')}
                  </Box>
                  {row.sharedWith.length > 0 && (
                    <Box
                      sx={{
                        fontSize: '11.5px',
                        color: colors.slate,
                        fontWeight: 600,
                        ...ELLIPSIS,
                      }}
                    >
                      {t('buildings_shared_with', {
                        buildings: row.sharedWith.join(', '),
                      })}
                    </Box>
                  )}
                </Box>
                <Box sx={CELL}>{row.places}</Box>
                <Box
                  sx={{
                    ...CELL,
                    color: row.overCapacity ? colors.red : colors.textBody,
                  }}
                  title={
                    row.overCapacity ? t('buildings_over_capacity') : undefined
                  }
                >
                  {row.reserved}
                </Box>
                <Box
                  sx={{
                    ...CELL,
                    color: futureDay ? colors.slate : colors.textBody,
                  }}
                  title={futureDay ? t('used_unknown_yet') : undefined}
                >
                  {futureDay ? '—' : row.used}
                </Box>
                <Box sx={{ ...CELL, color: colors.green }}>{row.free}</Box>
                {showUtilization && (
                  <Box sx={{ ...CELL, color: colors.textMuted }}>
                    {futureDay
                      ? '—'
                      : row.pct != null
                        ? `${row.pct}%`
                        : t('generic:none')}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {futureDay && rows.length > 0 && (
        <Box
          sx={{
            fontSize: '11.5px',
            color: colors.textMuted,
            fontWeight: 600,
            mt: '10px',
          }}
        >
          {t('used_unknown_yet')}
        </Box>
      )}

      {rows.some(row => row.sharedWith.length > 0) && (
        <Box
          sx={{
            fontSize: '11.5px',
            color: colors.textMuted,
            fontWeight: 600,
            mt: '10px',
          }}
        >
          {t('buildings_shared_footnote')}
        </Box>
      )}
    </Box>
  )
}

export default BuildingsTable
